/*
 * Purpose: Gmail API — fetch matching messages for gmail_inbound live runs
 * Last modified: 2026-06-25 | Agent
 * Completeness: 82
 */
import { getGmailAccessToken } from './gmailOAuth';
import type { GmailEvent } from './gmailInbound';

function decodeBase64Url(data: string): string {
  const padded = data.replace(/-/g, '+').replace(/_/g, '/');
  try {
    return decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
  } catch {
    return '';
  }
}

function headerValue(headers: Array<{ name: string; value: string }>, name: string): string {
  return headers.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value || '';
}

export async function fetchLatestGmailApiMessage(query = 'is:unread'): Promise<GmailEvent | null> {
  const token = getGmailAccessToken();
  if (!token) {
    throw new Error('Gmail not connected — use Connect Gmail or webhook inbound.');
  }

  const listUrl = new URL('https://gmail.googleapis.com/gmail/v1/users/me/messages');
  listUrl.searchParams.set('maxResults', '1');
  listUrl.searchParams.set('q', query);

  const listRes = await fetch(listUrl.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!listRes.ok) {
    throw new Error(`Gmail list failed: HTTP ${listRes.status}`);
  }

  const list = (await listRes.json()) as { messages?: Array<{ id: string }> };
  const messageId = list.messages?.[0]?.id;
  if (!messageId) return null;

  const msgRes = await fetch(
    `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}?format=full`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!msgRes.ok) {
    throw new Error(`Gmail get failed: HTTP ${msgRes.status}`);
  }

  const msg = (await msgRes.json()) as {
    payload?: {
      headers?: Array<{ name: string; value: string }>;
      body?: { data?: string };
      parts?: Array<{ mimeType?: string; body?: { data?: string } }>;
    };
  };

  const headers = msg.payload?.headers || [];
  const from = headerValue(headers, 'From');
  const subject = headerValue(headers, 'Subject');

  let body = '';
  if (msg.payload?.body?.data) {
    body = decodeBase64Url(msg.payload.body.data);
  } else if (msg.payload?.parts) {
    const textPart = msg.payload.parts.find((p) => p.mimeType === 'text/plain');
    if (textPart?.body?.data) body = decodeBase64Url(textPart.body.data);
  }

  return {
    from,
    subject,
    body,
    messageId,
    receivedAt: new Date().toISOString(),
    source: 'gmail_api',
    roles: [],
  };
}
